import { Check, Pencil, X } from "react-bootstrap-icons";
import { ReferenceItemDTO } from "../../../api/client";
import { useEffect, useRef, useState } from "react";

interface PillProps {
    item: ReferenceItemDTO;
    onRemove: (id: string) => void;
    onEdit: (id: string, newName: string) => void;
}
 
export default function ItemPill({ item, onRemove, onEdit }: PillProps) {
    const [isEditing, setIsEditing] = useState<boolean>(false);
    const [inputValue, setInputValue] = useState<string>(item.name!);
    const inputRef = useRef<HTMLInputElement>(null);
 
    const handleEdit = () => {
        const trimmed = inputValue.trim();
        if (trimmed && trimmed !== item.name) {
            onEdit(item.id!, trimmed);
        }
        setIsEditing(false);
    };
 
    useEffect(() => {
        setInputValue(item.name!);
    }, [item.name, isEditing]);
 
    useEffect(() => {
        if (isEditing) {
            inputRef.current?.focus();
        }
    }, [isEditing]);
 
    if (isEditing) {
        return (
            <div className="inline-flex box-border items-center gap-1 px-1 py-1 -translate-y-1 rounded-full border border-usfGreen bg-white dark:bg-gray-800 shadow-sm ring-2 ring-usfGreen dark:ring-usfStorm transition-all">
                <input
                    ref={inputRef}
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    placeholder={item.name}
                    className="text-sm font-medium bg-transparent outline-none px-2 min-w-16 text-gray-800 dark:text-gray-100 w-fit"
                    style={{ width: `${Math.max(inputValue.length, 1)}ch`, minWidth: `${Math.max(item.name?.length!, 10)}ch` }}
                />
                <div className="flex items-center gap-1">
                    {/* Did not use <Button></Button> component because these buttons are styled very particularly for the ItemPill component */}
                    <abbr title="Cancel editing. Your edits will not be saved.">
                        <button
                            onClick={() => {
                                setInputValue(item.name!);
                                setIsEditing(false);
                            }}
                            className="flex items-center justify-center w-6 h-6 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 dark:hover:text-gray-200 transition-colors"
                        >
                            <X className="w-5 h-5" />
                        </button>
                    </abbr>
                    <abbr title="Save your edit changes.">
                        <button
                            onClick={handleEdit}
                            className="flex items-center justify-center w-6 h-6 rounded-full text-white bg-usfGreen hover:bg-usfEvergreen transition-colors"
                        >
                            <Check className="w-5 h-5" />
                        </button>
                    </abbr>
                </div>
            </div>
        );
    }
 
    return (
        <div className="group inline-flex items-center pl-2 pr-1 py-1 rounded-full border gap-2 border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 hover:border-gray-300 dark:hover:border-gray-500 hover:bg-white dark:hover:bg-gray-600 transition-all cursor-default">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-200 select-none">
                {item.name}
            </span>
            <div className="flex w-0 overflow-hidden group-hover:w-10 transition-all items-center gap-1 opacity-0 group-hover:opacity-100">
                {/* Did not use <Button></Button> component because these buttons are styled very particularly for the ItemPill component */}
                <abbr title="Click to edit this item">
                    <button
                        onClick={() => setIsEditing(true)}
                        className="flex items-center justify-center w-5 h-5 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-500 dark:hover:text-gray-100 transition-all"
                    >
                        <Pencil className="w-4 h-4" />
                    </button>
                </abbr>
                <abbr title="Click to remove this item">
                    <button
                        onClick={() => onRemove(item.id!)}
                        className="flex items-center justify-center w-5 h-5 rounded-full text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/30 dark:hover:text-red-400 transition-all"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </abbr>
            </div>
        </div>
    );
}
 